
import type { CustomFunction } from '@sa/services/interfaces/customFunction';
import type { BatchDataRequest, BatchDataResponse, BatchMetrics } from '@sa/services/interfaces/batchRecalc';
import SessionStore from '$lib/services/sessionStore';
import { sendBatchData } from '$lib/services/recalcApi';
import { sendBatchMetrics } from '$lib/services/taskpaneApi';
import { HttpError, buildDialogUrl } from '$lib/providers/http';
import { showTaskpaneForOffice, waitForOfficeReady } from '$lib/office/office';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

/**
 * Tells the user to sign in, when a whole batch failed because the workbook has no usable token.
 *
 * A custom function cannot prompt for anything — it returns a value to a cell and that is all —
 * so without this the entire sheet fills with `#VALUE!` and nothing says why.
 *
 * `displayWebDialog` rather than `displayDialogAsync`: this runs in the custom-functions runtime,
 * which has no `Office.context.ui`.
 */
const showSignInInstructions = async (): Promise<void> => {
    try {
        const url = await buildDialogUrl('instructions');
        await OfficeRuntime.displayWebDialog(url, { height: '50', width: '33' });
    } catch (error) {
        // The dialog is advice, not the answer. Failing to show it must not replace the real
        // error the caller is about to see.
        console.error('Could not show the sign-in instructions', error);
    }
};

const BATCH_DELAY = 250;
const MAX_VOLUME = 1000;
/** How long a settled call waits for Office to become ready before handing Excel its result anyway. */
const RESULT_READY_TIMEOUT_MS = 15000;

export interface BatchMeta {
    fctn: CustomFunction;
    processed?: boolean;
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
}

export class BatchQueue {
    private currentBatch: Array<BatchMeta> = [];
    private callInProgress = false;
    private static instances: Map<string, BatchQueue> = new Map();

    private constructor() {}

    public static getQueue = (scottsOrgId: string) => {
        let queue = BatchQueue.instances.get(scottsOrgId);
        if (!queue) {
            queue = new BatchQueue();
            BatchQueue.instances.set(scottsOrgId, queue);
        }
        return queue;
    };

    private dequeueCalls = async (batch: Array<BatchMeta>): Promise<void> => {
        if (batch == null || batch.length == 0) return;
        const requests = [] as Array<BatchDataRequest>;
        try {
            // Check data.scottsorgid, if it is not in the session orglist, reject.
            const authedOrgs = await SessionStore.getAuthedOrgs();
            batch.forEach((call, i) => {
                const scottsOrgId: string = call.fctn.scottsOrgId;
                if (scottsOrgId && !authedOrgs.includes(scottsOrgId + '')) {
                    batch[i].reject('Org not authorized');
                    batch[i].processed = true;
                    return;
                }
                requests.push({
                    fctn: call.fctn,
                    index: i
                } as BatchDataRequest);
            });
        
            if (requests.length == 0) {
                return;
            }
        } catch (error) {
            // The error itself, not JSON: an Error stringifies to `{}`.
            console.error('dequeueCalls error:', error);
            for (let index = 0; index < batch.length; index++) {
                batch[index].reject(error);
            }
            return;
        }

        const metrics: BatchMetrics = {
            totalCt: batch.length,
            errCt: 0,
            nullCt: 0,
            noResCt: 0,
            successCt: 0,
            serverErrCt: 0,
            requestTimeMS: 0,
        };
        let perfStart = performance.now();
        let perfEnd = performance.now();
        try {
            console.log(`Recalc Call ${requests.length} of ${batch.length} calls.`);
            perfStart = performance.now();
            const batchResponse: Array<BatchDataResponse> = await sendBatchData(requests);
            perfEnd = performance.now();
            batchResponse.forEach((msgData) => {
                if (msgData && batch[msgData.index]) {
                    batch[msgData.index].processed = true;
                    if (msgData.error) {
                        batch[msgData.index].reject(msgData.error);
                        metrics.errCt++;
                    } else if (msgData.data === null) {
                        batch[msgData.index].reject('NULL received.');
                        metrics.nullCt++;
                    } else {
                        batch[msgData.index].resolve(msgData.data);
                        metrics.successCt++;
                    }
                }
            });
            // Reject the ones that were missing.
            for (let i = 0; i < batch.length; i++) {
                if (!batch[i].processed) {
                    batch[i].reject('No Response.');
                    metrics.noResCt++;
                }
            }
        } catch (error) {
            perfEnd = performance.now();
            console.error('dequeueCalls error:', error);
            // The whole thing died (likely auth related.)  Reject em all.
            for (let index = 0; index < batch.length; index++) {
                batch[index].reject(error);
            }
            metrics.serverErrCt = batch.length;
            // Only an auth failure gets the dialog. A 500, a timeout or a bad filter are all
            // things signing in again will not fix, and a dialog for them would be noise.
            if (error instanceof HttpError
                && (error.statusCode === HTTP_UNAUTHORIZED || error.statusCode === HTTP_FORBIDDEN)) {
                void showSignInInstructions();
            }
        } finally {
            metrics.requestTimeMS = perfEnd - perfStart;
            // Best effort: metrics must never surface as an unhandled rejection.
            sendBatchMetrics(metrics).catch((error: unknown) => console.error('Could not send batch metrics', error));
        }
    };

    public enqueueCall = async <T>(fctn: CustomFunction): Promise<T> => {
        const latin1Set = /^[\u0021-\u007E\u00C0-\u00FF\s]*$/;
        for (const val of Object.values(fctn)) {
            if (typeof val === 'string' && !latin1Set.test(val)) {
                return Promise.reject(new Error('Invalid characters.'));
            }
        }
        if (!Array.isArray(this.currentBatch)) {
            this.currentBatch = [];
        }
        
        const newCall: Partial<BatchMeta> = {
            fctn: fctn,
            processed: false,
            resolve: undefined,
            reject: undefined,
        };
        const promise: Promise<T> = new Promise((resolve, reject) => {
            newCall.resolve = resolve as unknown as (value: unknown) => void;
            newCall.reject = reject;
        });
        this.currentBatch.push(newCall as BatchMeta);

        if (this.currentBatch.length >= MAX_VOLUME) {
            const batch = this.currentBatch.splice(0, this.currentBatch.length);
            void this.dequeueCalls(batch);
        }
        
        if (!this.callInProgress) {
            this.callInProgress = true;
            setTimeout(() => {
                const batch = this.currentBatch.splice(0, this.currentBatch.length);
                void this.dequeueCalls(batch);
                this.callInProgress = false;
            }, BATCH_DELAY);
        }
        // Before Office is ready Excel doesn't apply a custom function's result: the cell stays
        // #BUSY! though the server answered (see `isOfficeReady`). Showing the taskpane makes Office
        // ready, so ask for it now, and settle each call only once Office is ready, or once
        // RESULT_READY_TIMEOUT_MS have passed, when it settles anyway rather than hold it forever.
        showTaskpaneForOffice('custom function called');
        return promise.catch((error) => {
            let errorMsg = 'An unknown error has occurred';
            if (error instanceof Error) {
                errorMsg = error.message;
            } else if (typeof error === 'string') {
                errorMsg = error;
            }
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, errorMsg);
        }).finally(() => waitForOfficeReady(RESULT_READY_TIMEOUT_MS));
    };
}

/**
 * data.filter === ''           : Filter for null or empty string  
 * data.filter === 'thing'      : Filter specific thing  
 * data.filter === undefined    : Ignore filter
 */
export const enqueueCall = async <T>(fctn: CustomFunction): Promise<T> => {
    return BatchQueue.getQueue(fctn.scottsOrgId).enqueueCall<T>(fctn);
};